import { useState } from 'react'
import { Users, Search, Loader2 } from 'lucide-react'
import { useTelegramContacts, useTelegramContactsCount, type TelegramBot } from '@/api/hooks/useTelegram'
import { BotSelector } from './BotSelector'

export function ContactsTab({ botId, bots, onBotChange }: { botId: string | null; bots: TelegramBot[]; onBotChange: (id: string) => void }) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { data: contactsData, isLoading } = useTelegramContacts(botId, debouncedSearch)
  const { data: countData } = useTelegramContactsCount(botId)

  const contacts = contactsData?.data || []
  const pagination = contactsData || {}

  let searchTimer: ReturnType<typeof setTimeout>
  const handleSearch = (val: string) => {
    setSearch(val)
    clearTimeout(searchTimer)
    searchTimer = setTimeout(() => setDebouncedSearch(val), 400)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <BotSelector bots={bots} value={botId} onChange={onBotChange} />
        {countData && <p className="text-sm text-muted-foreground">{countData.count} contato(s) ativos</p>}
      </div>

      {!botId ? (
        <p className="text-sm text-muted-foreground">Adicione um bot primeiro na aba Bots</p>
      ) : (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Buscar por nome ou @username..."
              className="w-full pl-10 pr-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</div>
          ) : contacts.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-8 text-center">
              <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{search ? 'Nenhum contato encontrado' : 'Nenhum contato capturado ainda'}</p>
              <p className="text-xs text-muted-foreground mt-1">Contatos são capturados quando enviam mensagens nos grupos do bot</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-accent/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Nome</th>
                    <th className="text-left px-4 py-2 font-medium">Username</th>
                    <th className="text-left px-4 py-2 font-medium">Telegram ID</th>
                    <th className="text-left px-4 py-2 font-medium">Visto em</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {contacts.map((c: any) => (
                    <tr key={c.id} className="hover:bg-accent/30">
                      <td className="px-4 py-2">{c.first_name} {c.last_name || ''}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.username ? `@${c.username}` : '—'}</td>
                      <td className="px-4 py-2 text-xs font-mono text-muted-foreground">{c.telegram_user_id}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{c.last_seen_at ? new Date(c.last_seen_at).toLocaleString('pt-BR') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination info */}
              {pagination.last_page > 1 && (
                <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground">
                  Página {pagination.current_page} de {pagination.last_page} ({pagination.total} total)
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
